// Resources
import React, { useMemo, useState } from 'react';
import SidePanel from './SidePanel';

// Icons
import { TfiBackRight } from 'react-icons/tfi';
import { FaCheckCircle } from 'react-icons/fa';
import { BsExclamationTriangleFill } from 'react-icons/bs';
import { IoIosArrowDropdown, IoIosArrowDropup } from 'react-icons/io';
import { HiOutlineArrowSmRight } from 'react-icons/hi';
import { FiPlus, FiEdit2, FiTrash2, FiPlay } from 'react-icons/fi';

import { NODE_THEMES } from '../Components/NodeTheme';
import { KitSelect } from '../Components/NodeFormKit';
import {
  runDiagnostics,
  blankRule,
  RULE_TEMPLATES,
  DIAGNOSTIC_TOKENS,
  OPERATORS,
  EXTRACTORS,
} from './diagnostics/engine';

const ACCENT = '#0a7ba5';
const SEV = { error: '#FF2242', warn: '#e0a32e', ok: '#2fae4e' };

// Tipos de nodo disponibles para segmentar una regla.
const NODE_TYPE_OPTIONS = [
  { value: '*', label: 'Todos los nodos' },
  { value: 'basicAnswer', label: 'Respuesta Básica' },
  { value: 'conditions', label: 'Condiciones' },
  { value: 'saveAnswer', label: 'Guardar Respuesta' },
  { value: 'queryApi', label: 'Consulta API' },
  { value: 'webHook', label: 'WebHook' },
  { value: 'json', label: 'JSON' },
  { value: 'template', label: 'Plantilla' },
  { value: 'tags', label: 'Etiquetas' },
  { value: 'assistant', label: 'Asistente IA' },
  { value: 'transferAgent', label: 'Transferir Agente' },
  { value: 'flowsWhatsApp', label: 'Flujo WhatsApp' },
];

const nodeTypeLabel = (value) =>
  NODE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value;

const DiagnosticsPanel = ({
  rules = [],
  onChangeRules,
  nodes = [],
  tokens = {},
  goToNode,
  findNodeColor,
  handleClose,
  animateClass,
  isDark,
}) => {
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;

  const [results, setResults] = useState(null); // null = aún no se ejecuta
  const [openRuleKey, setOpenRuleKey] = useState(null);
  const [editing, setEditing] = useState(null); // regla en edición (o null)

  // --- Estilos reutilizables (nativos, sin react-select para evitar clipping) ---
  const inputStyle = {
    width: '100%',
    height: '36px',
    padding: '0 10px',
    fontSize: '13px',
    color: theme.textPrimary,
    backgroundColor: theme.inputBg,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: '4px',
    display: 'block',
  };
  const chipBtn = (tone = ACCENT) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '8px',
    border: `1px solid color-mix(in srgb, ${tone} 35%, var(--su-surface, #ffffff))`,
    backgroundColor: `color-mix(in srgb, ${tone} 12%, var(--su-surface, #ffffff))`,
    color: theme.textPrimary,
    cursor: 'pointer',
  });

  // --- Acciones sobre reglas ---
  const commitRules = (next) => {
    setResults(null); // los resultados anteriores dejan de ser válidos
    onChangeRules(next);
  };
  const saveRule = (rule) => {
    const exists = rules.some((r) => r.id === rule.id);
    const next = exists
      ? rules.map((r) => (r.id === rule.id ? rule : r))
      : [...rules, rule];
    commitRules(next);
    setEditing(null);
  };
  const deleteRule = (id) => commitRules(rules.filter((r) => r.id !== id));
  const toggleRule = (id) =>
    commitRules(
      rules.map((r) => (r.id === id ? { ...r, enabled: r.enabled === false } : r))
    );
  const addTemplate = (tpl) => {
    commitRules([...rules, tpl.build()]);
  };

  // --- Ejecutar ---
  const runNow = () => {
    setResults(runDiagnostics(nodes, rules, tokens));
    setOpenRuleKey(null);
  };

  const summary = useMemo(() => {
    if (!results) return null;
    const failed = results.filter((r) => r.status === 'fail');
    const totalFailNodes = failed.reduce((a, r) => a + r.failCount, 0);
    const hasError = failed.some((r) => r.rule.severity === 'error');
    return { failed, totalFailNodes, hasError, healthy: failed.length === 0 };
  }, [results]);

  const healthColor = !summary
    ? theme.border
    : summary.healthy
    ? SEV.ok
    : summary.hasError
    ? SEV.error
    : SEV.warn;

  return (
    <SidePanel title="Diagnóstico" subtitle="Valida las reglas de este bot en un clic" onClose={handleClose}>
      {/* Cuerpo */}
      <div id="content-list-chats" style={{ overflowY: 'auto', padding: '14px' }}>
        {/* Botón ejecutar */}
        <button
          type="button"
          onClick={runNow}
          disabled={rules.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            width: '100%',
            padding: '10px 14px',
            borderRadius: '9px',
            border: 'none',
            backgroundColor: rules.length === 0 ? theme.inputBg : ACCENT,
            color: rules.length === 0 ? theme.textTertiary : '#fff',
            fontSize: '13px',
            fontWeight: 600,
            cursor: rules.length === 0 ? 'default' : 'pointer',
          }}
        >
          <FiPlay style={{ fontSize: '15px' }} />
          Ejecutar diagnóstico
        </button>

        {/* Resumen de resultados */}
        {summary && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '11px 12px',
              borderRadius: '10px',
              border: `1px solid color-mix(in srgb, ${healthColor} 35%, ${theme.panelBg})`,
              backgroundColor: `color-mix(in srgb, ${healthColor} 8%, ${theme.panelBg})`,
              margin: '14px 0',
            }}
          >
            {summary.healthy ? (
              <FaCheckCircle style={{ fontSize: '20px', color: SEV.ok, flexShrink: 0 }} />
            ) : (
              <BsExclamationTriangleFill
                style={{ fontSize: '18px', color: healthColor, flexShrink: 0 }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: theme.textPrimary }}>
                {summary.healthy
                  ? 'Todas las reglas pasaron'
                  : `${summary.failed.length} ${
                      summary.failed.length === 1 ? 'regla' : 'reglas'
                    } con fallos`}
              </div>
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                {summary.healthy
                  ? 'El bot cumple todo lo configurado.'
                  : `${summary.totalFailNodes} ${
                      summary.totalFailNodes === 1 ? 'nodo' : 'nodos'
                    } no cumplen.`}
              </div>
            </div>
          </div>
        )}

        {/* Detalle por regla (tras ejecutar) */}
        {results && (
          <div style={{ marginBottom: '6px' }}>
            {results.map((res) => {
              const sevColor = SEV[res.rule.severity] || SEV.error;
              const dot =
                res.status === 'pass'
                  ? SEV.ok
                  : res.status === 'skipped'
                  ? theme.textTertiary
                  : sevColor;
              const isOpen = openRuleKey === res.rule.id;
              const canOpen = res.status === 'fail';
              return (
                <div key={res.rule.id} style={{ marginTop: '8px' }}>
                  <div
                    onClick={() => canOpen && setOpenRuleKey(isOpen ? null : res.rule.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '9px 11px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.inputBg,
                      cursor: canOpen ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: dot,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: theme.textPrimary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {res.rule.name || '(sin nombre)'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {res.status === 'skipped' ? (
                        <span style={{ fontSize: '12px', color: theme.textTertiary }}>
                          sin nodos
                        </span>
                      ) : res.status === 'pass' ? (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: SEV.ok }}>
                          {res.passCount}/{res.applicableCount} ✓
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: sevColor,
                            backgroundColor: `color-mix(in srgb, ${sevColor} 15%, var(--su-surface, #ffffff))`,
                            borderRadius: '999px',
                            padding: '1px 7px',
                          }}
                        >
                          {res.failCount} ✕
                        </span>
                      )}
                      {canOpen &&
                        (isOpen ? (
                          <IoIosArrowDropup style={{ fontSize: '16px', color: theme.textSecondary }} />
                        ) : (
                          <IoIosArrowDropdown style={{ fontSize: '16px', color: theme.textSecondary }} />
                        ))}
                    </div>
                  </div>

                  {/* Nodos que fallaron */}
                  {isOpen && canOpen && (
                    <div style={{ padding: '8px 2px 2px 2px' }}>
                      {res.failures.map((f) => {
                        const nodeColor = findNodeColor
                          ? findNodeColor(f.node.type)
                          : ACCENT;
                        return (
                          <button
                            key={f.node.id}
                            type="button"
                            onClick={() => goToNode(f.node)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px',
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 10px',
                              marginBottom: '6px',
                              borderRadius: '8px',
                              border: `1px solid ${theme.border}`,
                              borderLeft: `3px solid ${nodeColor}`,
                              backgroundColor: theme.panelBg,
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: theme.textPrimary,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {f.node.data?.title || f.node.id}
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: theme.textSecondary,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {f.reason}
                              </div>
                            </div>
                            <HiOutlineArrowSmRight
                              style={{ fontSize: '16px', color: theme.textTertiary, flexShrink: 0 }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Separador / sección de reglas */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '18px',
            marginBottom: '8px',
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 600, color: theme.textSecondary }}>
            Reglas ({rules.length})
          </span>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(blankRule())}
              style={chipBtn(ACCENT)}
            >
              <FiPlus style={{ fontSize: '13px' }} />
              Agregar
            </button>
          )}
        </div>

        {/* Editor de regla inline */}
        {editing && (
          <RuleEditor
            key={editing.id}
            initial={editing}
            theme={theme}
            inputStyle={inputStyle}
            labelStyle={labelStyle}
            onCancel={() => setEditing(null)}
            onSave={saveRule}
          />
        )}

        {/* Lista de reglas configuradas */}
        {!editing && rules.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '20px 12px',
              color: theme.textTertiary,
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 600, color: theme.textSecondary }}>
              Sin reglas todavía
            </div>
            <div style={{ fontSize: '12px', marginTop: '2px', marginBottom: '12px' }}>
              Crea una regla o usa una plantilla.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {RULE_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  type="button"
                  onClick={() => addTemplate(tpl)}
                  style={{
                    ...chipBtn(theme.textSecondary),
                    justifyContent: 'center',
                    border: `1px dashed ${theme.border}`,
                    backgroundColor: theme.inputBg,
                  }}
                >
                  <FiPlus style={{ fontSize: '13px' }} />
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!editing &&
          rules.map((rule) => {
            const sevColor = SEV[rule.severity] || SEV.error;
            const disabled = rule.enabled === false;
            return (
              <div
                key={rule.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  padding: '9px 11px',
                  marginBottom: '7px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.inputBg,
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: sevColor,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: theme.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {rule.name || '(sin nombre)'}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: theme.textSecondary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {nodeTypeLabel(rule.nodeType)} · {rule.field}
                  </div>
                </div>
                {/* Toggle activo */}
                <button
                  type="button"
                  title={disabled ? 'Activar' : 'Desactivar'}
                  onClick={() => toggleRule(rule.id)}
                  style={{
                    width: '30px',
                    height: '18px',
                    borderRadius: '999px',
                    border: 'none',
                    padding: 0,
                    flexShrink: 0,
                    cursor: 'pointer',
                    backgroundColor: disabled ? theme.border : `${ACCENT}`,
                    position: 'relative',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: disabled ? '2px' : '14px',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      transition: 'left 0.15s ease',
                    }}
                  />
                </button>
                <FiEdit2
                  title="Editar"
                  onClick={() => setEditing(rule)}
                  style={{ fontSize: '14px', color: theme.textSecondary, cursor: 'pointer', flexShrink: 0 }}
                />
                <FiTrash2
                  title="Eliminar"
                  onClick={() => deleteRule(rule.id)}
                  style={{ fontSize: '14px', color: SEV.error, cursor: 'pointer', flexShrink: 0 }}
                />
              </div>
            );
          })}
      </div>
    </SidePanel>
  );
};

// ---------------------------------------------------------------------------
// Editor de una regla (formulario nativo, controlado localmente).
// ---------------------------------------------------------------------------
const RuleEditor = ({ initial, theme, inputStyle, labelStyle, onCancel, onSave }) => {
  const [draft, setDraft] = useState(initial);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'auto' };
  const showExpected = draft.operator !== 'notEmpty';
  const canSave = draft.name.trim() && draft.field.trim();

  const insertToken = (token) =>
    set({ expected: `${draft.expected || ''}${token}` });

  return (
    <div
      style={{
        padding: '12px',
        borderRadius: '10px',
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.panelBg,
        marginBottom: '10px',
      }}
    >
      <div style={{ marginBottom: '10px' }}>
        <label style={labelStyle}>Nombre de la regla</label>
        <input
          style={inputStyle}
          value={draft.name}
          placeholder="Ej. WebHook apunta a este bot"
          onChange={(e) => set({ name: e.target.value })}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Aplica en</label>
          <KitSelect theme={theme} ariaLabel="Aplica en" value={draft.nodeType} onChange={(value) => set({ nodeType: value })} options={NODE_TYPE_OPTIONS} />
        </div>
        <div style={{ width: '150px' }}>
          <label style={labelStyle}>Severidad</label>
          <KitSelect theme={theme} ariaLabel="Severidad" value={draft.severity} onChange={(value) => set({ severity: value })} options={[{ value: 'error', label: 'Error' }, { value: 'warn', label: 'Advertencia' }]} />
        </div>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label style={labelStyle}>Campo (path dentro del nodo)</label>
        <input
          style={inputStyle}
          value={draft.field}
          placeholder="url · body · variables.company.id_company"
          onChange={(e) => set({ field: e.target.value })}
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label style={labelStyle}>Cómo obtener el valor</label>
        <KitSelect theme={theme} ariaLabel="Cómo obtener el valor" value={draft.extractor} onChange={(value) => set({ extractor: value })} options={EXTRACTORS} />
      </div>

      {draft.extractor === 'urlSegment' && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Después de</label>
            <input
              style={inputStyle}
              value={draft.segAfter}
              placeholder="webhook/"
              onChange={(e) => set({ segAfter: e.target.value })}
            />
          </div>
          <div style={{ width: '80px' }}>
            <label style={labelStyle}>Índice</label>
            <input
              style={inputStyle}
              type="number"
              min={0}
              value={draft.segIndex}
              onChange={(e) => set({ segIndex: Number(e.target.value) })}
            />
          </div>
        </div>
      )}

      <div style={{ marginBottom: showExpected ? '10px' : '14px' }}>
        <label style={labelStyle}>Condición</label>
        <select
          style={selectStyle}
          value={draft.operator}
          onChange={(e) => set({ operator: e.target.value })}
        >
          {OPERATORS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {showExpected && (
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Valor esperado</label>
          <input
            style={inputStyle}
            value={draft.expected}
            placeholder="Texto o token"
            onChange={(e) => set({ expected: e.target.value })}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
            {DIAGNOSTIC_TOKENS.map((t) => (
              <button
                key={t.token}
                type="button"
                title={t.label}
                onClick={() => insertToken(t.token)}
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.inputBg,
                  color: theme.textSecondary,
                  cursor: 'pointer',
                }}
              >
                {t.token}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '7px 16px',
            fontSize: '12px',
            fontWeight: 500,
            borderRadius: '8px',
            border: `1px solid ${theme.border}`,
            backgroundColor: 'transparent',
            color: theme.textSecondary,
            cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSave(draft)}
          style={{
            padding: '7px 18px',
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '8px',
            border: 'none',
            backgroundColor: canSave ? ACCENT : theme.inputBg,
            color: canSave ? '#fff' : theme.textTertiary,
            cursor: canSave ? 'pointer' : 'default',
          }}
        >
          Guardar regla
        </button>
      </div>
    </div>
  );
};

export default DiagnosticsPanel;
